/**
 * PathwayCanvasMinimap (Sprint 4.3.5).
 *
 * Thin wrapper around React Flow's built-in <MiniMap> that pins it to
 * the bottom-left of the canvas with a token-driven node colour map per
 * the Awell Studio canvas reference. Must be rendered as a child of
 * <ReactFlow>; the parent canvas in `PathwayCanvas.tsx` positions it.
 *
 * HC2 token-only. Colour values are hard-coded only because React Flow's
 * MiniMap props expect concrete CSS colours rather than Tailwind class
 * names; the values mirror the Signal DS tokens in
 * `src/styles/tokens.ts`. Any token drift will surface in the
 * `pathway-canvas-minimap` test snapshot.
 */
import { MiniMap, type Node } from 'reactflow';

const MINIMAP_NODE_COLOR_BY_TYPE: Record<string, string> = {
  // Mirrors `signal-primary-300`.
  track: '#8aa6ff',
  // Mirrors `surface-base` (white) with a faint tint via fill.
  step: '#ffffff',
  // Mirrors `signal-primary-100`.
  action: '#dbe5ff',
};

function mapNodeColor(node: Node): string {
  const type = node.type ?? 'default';
  return MINIMAP_NODE_COLOR_BY_TYPE[type] ?? '#cbd5e1';
}

const MINIMAP_NODE_STROKE_COLOR = '#e2e8f0'; // surface-border
const MINIMAP_MASK_COLOR = 'rgba(15, 23, 42, 0.06)'; // text-primary @ 6%

export function PathwayCanvasMinimap() {
  return (
    <MiniMap
      data-testid="pathway-canvas-minimap"
      position="bottom-left"
      nodeColor={mapNodeColor}
      nodeStrokeColor={MINIMAP_NODE_STROKE_COLOR}
      maskColor={MINIMAP_MASK_COLOR}
      pannable
      zoomable
    />
  );
}
